import { useEffect, useRef, useState } from "react";
import { getAdminHeaders, uploadAdminImage, toAssetUrl } from "./adminApi";

const CONTACT_WECHAT_KEY = "event_default_contact_wechat";
const CONTACT_PHONE_KEY = "event_default_contact_phone";

const emptyEvent = {
  title: "",
  summary: "",
  content: "",
  category: "mixer",
  city: "",
  venue: "",
  address: "",
  start_at: "",
  end_at: "",
  fee_label: "免费",
  quota_label: "",
  contact_wechat: "",
  contact_phone: "",
  signup_note: "",
  cover_url: "",
  is_enabled: 1,
  is_featured: 0,
  sort_order: 0,
};

function loadSavedContacts() {
  return {
    contact_wechat: localStorage.getItem(CONTACT_WECHAT_KEY) || "",
    contact_phone: localStorage.getItem(CONTACT_PHONE_KEY) || "",
  };
}

function saveContacts(wechat, phone) {
  if (wechat) localStorage.setItem(CONTACT_WECHAT_KEY, wechat);
  if (phone) localStorage.setItem(CONTACT_PHONE_KEY, phone);
}

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [defaultContacts, setDefaultContacts] = useState(loadSavedContacts);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [message, setMessage] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [contentUploading, setContentUploading] = useState(false);
  const [posterParsing, setPosterParsing] = useState(false);
  const coverInputRef = useRef(null);
  const contentInputRef = useRef(null);
  const posterInputRef = useRef(null);
  const contentTextareaRef = useRef(null);
  const pageSize = 20;

  const withOwnContacts = (base = {}) => {
    const saved = loadSavedContacts();
    return {
      ...base,
      contact_wechat:
        base.contact_wechat ||
        saved.contact_wechat ||
        defaultContacts.contact_wechat ||
        "",
      contact_phone:
        base.contact_phone ||
        saved.contact_phone ||
        defaultContacts.contact_phone ||
        "",
    };
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/events?page=${page}&page_size=${pageSize}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setCategories(data.categories || {});
        if (data.default_contacts) {
          const merged = {
            contact_wechat:
              loadSavedContacts().contact_wechat ||
              data.default_contacts.contact_wechat ||
              "",
            contact_phone:
              loadSavedContacts().contact_phone ||
              data.default_contacts.contact_phone ||
              "",
          };
          setDefaultContacts(merged);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [page]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm(
      withOwnContacts({
        ...emptyEvent,
        sort_order: total + 1,
      })
    );
  };

  const openEdit = (item) => {
    setEditing(item);
    setCreating(false);
    setForm({
      title: item.title || "",
      summary: item.summary || "",
      content: item.content || "",
      category: item.category || "mixer",
      city: item.city || "",
      venue: item.venue || "",
      address: item.address || "",
      start_at: item.start_at || "",
      end_at: item.end_at || "",
      fee_label: item.fee_label || "免费",
      quota_label: item.quota_label || "",
      contact_wechat: item.contact_wechat || "",
      contact_phone: item.contact_phone || "",
      signup_note: item.signup_note || "",
      cover_url: item.cover_url || "",
      is_enabled: item.is_enabled,
      is_featured: item.is_featured,
      sort_order: item.sort_order,
    });
  };

  const handlePosterSmart = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterParsing(true);
    setMessage("");
    try {
      const saved = loadSavedContacts();
      const body = new FormData();
      body.append("file", file);
      body.append(
        "contact_wechat",
        form.contact_wechat || saved.contact_wechat || defaultContacts.contact_wechat || ""
      );
      body.append(
        "contact_phone",
        form.contact_phone || saved.contact_phone || defaultContacts.contact_phone || ""
      );
      const res = await fetch("/api/admin/events/parse-poster", {
        method: "POST",
        headers: getAdminHeaders(),
        body,
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join("；")
          : detail || "识图失败";
        throw new Error(msg);
      }
      const fields = data.fields || {};
      setCreating(true);
      setEditing(null);
      setForm((prev) => ({
        ...prev,
        ...fields,
        is_enabled: 1,
        is_featured: prev.is_featured || 0,
        sort_order: prev.sort_order || total + 1,
        contact_wechat:
          fields.contact_wechat ||
          prev.contact_wechat ||
          saved.contact_wechat ||
          defaultContacts.contact_wechat ||
          "",
        contact_phone:
          fields.contact_phone ||
          prev.contact_phone ||
          saved.contact_phone ||
          defaultContacts.contact_phone ||
          "",
      }));
      setMessage(data.note || "已根据海报自动填写，请核对后保存发布。");
    } catch (err) {
      alert(err.message || "识图失败");
    } finally {
      setPosterParsing(false);
      if (posterInputRef.current) posterInputRef.current.value = "";
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, cover_url: url }));
    } catch (err) {
      alert(err.message || "上传失败");
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const insertContentImage = (url) => {
    const snippet = `\n[[IMG:${url}]]\n`;
    const el = contentTextareaRef.current;
    setForm((prev) => {
      const text = prev.content || "";
      if (!el) return { ...prev, content: `${text}${snippet}` };
      const start = el.selectionStart ?? text.length;
      const end = el.selectionEnd ?? text.length;
      const next = `${text.slice(0, start)}${snippet}${text.slice(end)}`;
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + snippet.length;
        el.setSelectionRange(pos, pos);
      });
      return { ...prev, content: next };
    });
  };

  const handleContentImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setContentUploading(true);
    try {
      const url = await uploadAdminImage(file);
      insertContentImage(url);
    } catch (err) {
      alert(err.message || "上传失败");
    } finally {
      setContentUploading(false);
      if (contentInputRef.current) contentInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      alert("请填写活动标题");
      return;
    }
    saveContacts(form.contact_wechat, form.contact_phone);
    const payload = {
      ...form,
      is_enabled: Number(form.is_enabled),
      is_featured: Number(form.is_featured),
      sort_order: Number(form.sort_order) || 0,
    };
    const url = editing ? `/api/admin/events/${editing.id}` : "/api/admin/events";
    const res = await fetch(url, {
      method: editing ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setEditing(null);
    setCreating(false);
    setMessage("活动已保存，前台首页与活动页会展示已启用的活动");
    loadItems();
  };

  const remove = async (item) => {
    if (!window.confirm(`删除活动「${item.title}」？`)) return;
    await fetch(`/api/admin/events/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    setMessage("已删除");
    loadItems();
  };

  const showModal = creating || editing;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">活动发布</h1>
          <p className="text-sm text-gray-500 mt-1">
            可直接上传活动海报，AI 自动填写标题/时间/地点；海报上的二维码不用，报名用你自己的微信/电话。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={posterInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePosterSmart}
          />
          <button
            type="button"
            disabled={posterParsing}
            onClick={() => posterInputRef.current?.click()}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {posterParsing ? "识别中..." : "上传海报智能发布"}
          </button>
          <button onClick={openCreate} className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm">
            手工发布
          </button>
        </div>
      </div>

      {message && <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">{message}</div>}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left p-3">标题</th>
              <th className="text-left p-3">类型</th>
              <th className="text-left p-3">城市</th>
              <th className="text-left p-3">时间</th>
              <th className="text-left p-3">状态</th>
              <th className="text-left p-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-gray-400">
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-gray-400">
                  暂无活动，点击右上角「上传海报智能发布」
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3 font-medium">{item.title}</td>
                  <td className="p-3">{item.category_label}</td>
                  <td className="p-3">{item.city || "—"}</td>
                  <td className="p-3">{item.start_at || "待定"}</td>
                  <td className="p-3">
                    {item.is_enabled ? item.status_label : "已隐藏"}
                    {item.is_featured ? " · 首页推荐" : ""}
                  </td>
                  <td className="p-3 space-x-2">
                    <button type="button" onClick={() => openEdit(item)} className="text-blue-600">
                      编辑
                    </button>
                    <button type="button" onClick={() => remove(item)} className="text-red-500">
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-sm text-gray-500 self-center">
            {page} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-3">
            <h3 className="font-bold">{editing ? "编辑活动" : "发布活动"}</h3>

            {!editing && (
              <div className="rounded-xl border border-violet-100 bg-violet-50/70 p-3 space-y-2">
                <p className="text-sm text-violet-900 font-medium">智能填表</p>
                <p className="text-xs text-violet-800 leading-5">
                  上传单身营会等海报，自动识别标题、时间、地点。海报二维码会被忽略，报名用你自己的联系方式。
                </p>
                <button
                  type="button"
                  disabled={posterParsing}
                  onClick={() => posterInputRef.current?.click()}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {posterParsing ? "识别中..." : "选海报识图填写"}
                </button>
              </div>
            )}

            <label className="block text-sm">
              <span className="text-gray-600">标题</span>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">类型</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              >
                {Object.entries(categories).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {[
              ["summary", "一句话简介"],
              ["city", "城市"],
              ["venue", "场地名称"],
              ["address", "详细地址"],
              ["start_at", "开始时间（如 2026-09-01 19:00）"],
              ["end_at", "结束时间"],
              ["fee_label", "费用说明"],
              ["quota_label", "名额说明"],
              ["contact_wechat", "报名微信（你自己的，不用海报二维码）"],
              ["contact_phone", "报名电话"],
            ].map(([key, label]) => (
              <label key={key} className="block text-sm">
                <span className="text-gray-600">{label}</span>
                <input
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                />
              </label>
            ))}
            <div className="block text-sm space-y-2">
              <span className="text-gray-600">活动详情</span>
              <textarea
                ref={contentTextareaRef}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={8}
                className="mt-1 w-full border rounded-lg px-3 py-2"
                placeholder="可写文字，并用下方按钮把图片插到光标位置"
              />
              <input
                ref={contentInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleContentImageUpload}
              />
              <button
                type="button"
                onClick={() => contentInputRef.current?.click()}
                disabled={contentUploading}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              >
                {contentUploading ? "上传中..." : "插入详情图片"}
              </button>
              <p className="text-xs text-gray-400">
                封面图单独上传。详情里可多次插入图片，会显示在正文中间。
              </p>
            </div>
            <label className="block text-sm">
              <span className="text-gray-600">报名说明</span>
              <textarea
                value={form.signup_note}
                onChange={(e) => setForm({ ...form, signup_note: e.target.value })}
                rows={3}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
            <div className="block text-sm space-y-2">
              <span className="text-gray-600">封面图</span>
              {form.cover_url ? (
                <img
                  src={toAssetUrl(form.cover_url)}
                  alt="封面"
                  className="w-full h-32 object-cover rounded-lg border"
                />
              ) : (
                <p className="text-xs text-gray-400">未上传封面</p>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
                >
                  {coverUploading ? "上传中..." : "上传封面"}
                </button>
                {form.cover_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_url: "" })}
                    className="px-3 py-1.5 text-red-500 text-sm"
                  >
                    移除
                  </button>
                )}
              </div>
            </div>
            <label className="block text-sm">
              <span className="text-gray-600">排序（越小越靠前）</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Number(form.is_enabled) === 1}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked ? 1 : 0 })}
              />
              前台展示
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Number(form.is_featured) === 1}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })}
              />
              首页优先推荐
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                取消
              </button>
              <button
                type="button"
                onClick={save}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm"
              >
                保存发布
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
